import { SSFBarsProvider } from 'components/SSFBars/SSFBars.context';
import { useSSFBehaviors } from 'hooks';
import { usePathname } from 'hooks/usePathname';
import { DateTime } from 'luxon';
import { dashboardAvailableShiftsPageConfig } from 'pages/DashboardAvailableShiftsPage/DashboardAvailableShiftsPage.constants';
import { dashboardMyUpcomingShiftsPageConfig } from 'pages/DashboardMyUpcomingShiftsPage/DashboardMyUpcomingShiftsPage.constants';
import { DashboardPageRouteType } from 'pages/DashboardPage/AgencyDashboardPage';
import { DashboardPageConfigMapType } from 'pages/DashboardPage/DashboardPage.types';
import { dashboardPastShiftsPageConfig } from 'pages/DashboardPastShiftsPage/DashboardPastShiftsPage.constants';
import { dashboardRecentlyCreatedShiftsPageConfig } from 'pages/DashboardRecentlyCreatedShiftsPage/DashboardRecentlyCreatedShiftsPage.constants';
import { dashboardUpcomingShiftsPageConfig } from 'pages/DashboardUpcomingShiftsPage/DashboardUpcomingShiftsPage.constants';
import { useAuthRouteDataContext } from 'providers/AuthRouteDataProvider/AuthRouteDataProvider.context';
import { FC, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { isUserAgencyAndAgencyLocationFilterForcedDisabledSelector } from 'store/selectors/featureFlagSelectors';
import { GQLAPIContextType, ISO8601Type, SSFBehaviorsValueType } from 'types/common.types';
import {
  DashboardAvailableShiftsPageQuery,
  DashboardPastShiftsPageQuery,
  DashboardRecentlyCreatedShiftsPageQuery,
  DashboardUpcomingShiftsPageQuery,
} from 'types/graphql';
import { createGQLDataContext } from 'utils/createDataContext';
import { withMemo } from 'utils/withMemo';

export const DashboardPageContext = createGQLDataContext();

const dashboardPageConfigMap: DashboardPageConfigMapType = {
  // B2B config
  '/dashboard/upcoming': dashboardUpcomingShiftsPageConfig,
  '/dashboard/recent': dashboardRecentlyCreatedShiftsPageConfig,
  '/dashboard/past': dashboardPastShiftsPageConfig,

  // Agency config
  '/dashboard/available': dashboardAvailableShiftsPageConfig,
  '/dashboard/myupcoming': dashboardMyUpcomingShiftsPageConfig,
};

function getNumFiltersApplied(values: SSFBehaviorsValueType) {
  let numFiltersApplied = 0;
  if (values.employmentStatus) numFiltersApplied += values.employmentStatus.length;
  if (values.shiftStatus) numFiltersApplied += values.shiftStatus.length;
  if (values.position_id) numFiltersApplied += values.position_id.length;
  if (values.internalJaneType) numFiltersApplied += values.internalJaneType.length;
  if (values.externalJaneType) numFiltersApplied += values.externalJaneType.length;
  if (values.actionsRequiredType) numFiltersApplied += values.actionsRequiredType.length;
  if (values.timeFrameType) numFiltersApplied += values.timeFrameType.length;
  return numFiltersApplied;
}

const _DashboardPageProvider: FC = ({ children }) => {
  const activeTab = usePathname() as DashboardPageRouteType;
  const [selectedDay, setSelectedDay] = useState<ISO8601Type | null>(null);
  const { locationFilter } = useAuthRouteDataContext();
  const isUserAgencyAndAgencyLocationFilterForcedDisabled = useSelector(
    isUserAgencyAndAgencyLocationFilterForcedDisabledSelector,
  );

  const [values, opts] = useSSFBehaviors(
    dashboardPageConfigMap[activeTab]({
      location: locationFilter.initialValue,
      // agency_id:
      isUserAgencyAndAgencyLocationFilterForcedDisabled,
    }),
  );
  const isOutsideOfMonth = useCallback(
    (date: string) => {
      const { filterDate } = values;
      const diff = DateTime.fromISO(filterDate[1]).diff(
        DateTime.fromISO(filterDate[0]),
        'days',
      ).days;
      const mid = DateTime.fromISO(filterDate[0]).plus({ days: diff / 2 });
      return !DateTime.fromISO(date).hasSame(mid, 'month');
    },
    [values],
  );

  const value = {
    values,
    numFiltersApplied: getNumFiltersApplied(values),
    Consumer: DashboardPageContext.Consumer,
    Provider: DashboardPageContext.Provider,
    isOutsideOfMonth,
    selectedDay,
    setSelectedDay,
    ...opts,
  };
  return (
    <DashboardPageContext.Provider value={value}>
      <SSFBarsProvider>{children}</SSFBarsProvider>
    </DashboardPageContext.Provider>
  );
};

export const DashboardPageProvider: FC = withMemo(_DashboardPageProvider);

export function useDashboardPageContext<
  T extends
    | DashboardAvailableShiftsPageQuery
    | DashboardPastShiftsPageQuery
    | DashboardRecentlyCreatedShiftsPageQuery
    | DashboardUpcomingShiftsPageQuery,
>(): GQLAPIContextType<T> {
  return useContext(DashboardPageContext);
}
